import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import SaleManager from 'app/src/payments/SaleManager';
import DepositManager from 'app/src/payments/DepositManager';
import PaymentTotalView from 'app/src/views/payments/PaymentTotalView';
import Decimal from 'app/src/data/base/Decimal';
import GlobalEvents from 'app/GlobalEvents';

export default class PaymentTotalViewController extends BasePaymentController {
  constructor(saleManager) {
    super(saleManager);

    this.view = new PaymentTotalView({
      el: 'div.register-payment-detail', // attach to the existing element
      model: this._saleManager.sale,
      viewData: {
        saleData: this._saleManager.toJSON(),
        isTipEnabled: this._saleManager.isTipEnabled,
      },
    });
  }

  /**
   * Initializes and displays the payment total view.  Binds to sale manager events.
   */
  show() {
    super.show();

    this.listenTo(this._saleManager, SaleManager.Events.PAYMENT_TOTAL_CHANGED, this.onTotalChanged);
    this.listenTo(this._saleManager, SaleManager.Events.BALANCE_CHANGED, this.onBalanceChanged);
    this.listenTo(this._saleManager, SaleManager.Events.CALCTIPS_CHANGED, this.onCalcTipsChanged);
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_FAILED,
      this.setInitialViewState
    );
    this.listenTo(this._saleManager, SaleManager.Events.SALE_REFRESHED, this.setInitialViewState);

    // refresh the view when deposits have been added or removed
    this.listenTo(
      this._saleManager.deposits,
      DepositManager.Events.DEPOSITS_CHANGED,
      this.onDepositsChanged
    );
  }

  /**
   * Sets the initial state of dynamic data within the view.
   */
  setInitialViewState() {
    const calcTips = this._saleManager.sale.get('calcTips') || 0;

    this._setViewPaymentTotal();

    this._setViewBalance(this._saleManager.pendingBalance);
    this.view.setDepositAmount(this._saleManager.deposits.depositTotal);
    this.view.showDeposits(!this._saleManager.deposits.depositTotal.isZero());
    this.view.setDepositRefundAmount(this._saleManager.deposits.refundTotal);
    this.view.showDepositRefunds(!this._saleManager.deposits.refundTotal.isZero());

    this.view.updateSaleTotal(
      this.view.viewData.saleData.saleTotal.plus(this._saleManager.deposits.depositTotal)
    );

    this.view.updateTipAmount(calcTips);
  }

  /**
   * Event handler for when the sale manager total has changed
   */
  onTotalChanged() {
    this._setViewPaymentTotal();
  }

  /**
   * Event handler for when the sale manager balance has changed
   *
   * @param balance {Decimal} The new sale balance
   */
  onBalanceChanged(balance) {
    this._setViewBalance(balance);
  }

  /**
   * Event handler for when the sale manager calcTips has changed
   *
   * @param calcTips {Decimal} The new calcTips
   */
  onCalcTipsChanged(calcTips) {
    this.view.updateTipAmount(calcTips);
  }

  /**
   * Event handler for when deposits in the sale manager have changed
   */
  onRemoveDepositsClicked() {
    this._saleManager.removeDeposits();
  }

  /**
   * Event handler for when a deposit is made
   *
   * @param {Boolean} isAccountPayment true if the deposit was an account payment
   */
  onDepositsChanged(isAccountPayment) {
    this.setInitialViewState();
    this.view.setDepositTitle(isAccountPayment ? 'Account Payment' : 'Deposit');
  }

  /**
   * Updates the view's balance
   *
   * @param balance {Decimal} The sale balance
   * @private
   */
  _setViewBalance(balance) {
    let displayAmount = balance;

    if (!this._saleManager.isRefundSale()) {
      // the existing view likes to show 0 balance when change is due;
      // let's keep it the same for
      displayAmount = Decimal.max(0, balance);
    }

    this.view.updateBalance(displayAmount);
  }

  /**
   * Sets the payment total within the view.
   *
   * @private
   */
  _setViewPaymentTotal() {
    this.view.updateTotal(this._saleManager.pendingPaymentsTotal);
  }
}
